/**
 * 路由配置参数说明
 *
 * 在运营平台配置的路由需要与路由地址相匹配
 *
 * path：路由的地址，不需要加父路由的那一部分
 * name：路由名称，路由跳转使用
 * component：路由对应的页面组件在文件中的路径
 * type：路由类型（目录 | 菜单）
 * meta：元数据
 *    - title：路由对应的中文名称
 *    - hidden：路由是否隐藏，默认为false
 *    - icon：路由的图标
 *    - isExternal：是否为外部链接，为true时，component以http|https开头，默认为false
 *    - needFormKey：是否在路由中生成formkey的query参数，默认为false
 *    - cache：是否缓存该路由对应的页面，默认为false
 *    - isMicro：是否为微服务路由，默认为false
 *    - grayscale：<string> 灰度标识
 *    - layout：Layout，会默认继承父路由的配置，若都没有定义则默认为DefaultLayout
 *    - pageComponentName：当路由名与页面组件名称不同时，且需要做缓存的时候，写入页面组件名称
 * applicableCompanyTypes：Array<Object> 适用主体（企业 | 事业单位 | 个体工商户 | 自然人），路由只会在适用的主体下面展示
 *    - companyType：企业类型 详看数据字典 companyTypeDict
 *    - menuName：菜单名称（不必须）类似meta的title，但优先级比它高，但是比platformAlias低
 * platformAlias：Array<Object> 平台别称，该路由在不同平台（云采购平台 | 省投平台等）展示不同的菜单名；类似meta的title，但优先级比它高
 *    - platformId：平台id 详看数据字典 platformTitleDict
 *    - platformName：平台名称（不必须）
 *    - menuName：平台展示的路由名称
 * applicableCompanyIdentities：适用身份（采购人|代理机构|供应商）详看数据字典platformServiceDict
 * roles：适用角色 详看数据字典platformFunctionModuleDict
 * superviseInstitutions：监管机构列表 详看 superviseInstitutionDict
 */

/**
 * @typedef RouteMetaConfig
 * @property {string} title 路由对应的中文名称
 * @property {boolean} [hidden=false] 路由是否隐藏，默认为false
 * @property {string} [icon] 路由对应的图标
 * @property {string} [layout=DefaultLayout] 布局名称，默认为DefaultLayout
 * @property {boolean} [isExternal=false] 是否为外部链接，为true时，component以http|https开头，默认为false
 * @property {boolean} [needFormKey=false] 是否在路由中生成formkey的query参数，默认为false
 * @property {boolean} [cache=false] 是否缓存该路由对应的页面，默认为false
 * @property {boolean} [isMicro=false] 是否为微服务路由，默认为false
 * @property {string} [permission] 权限标识
 */

/**
 * @typedef RouteApplicableCompanyType
 * @property {number} companyType 企业类型 详看数据字典 companyTypeDict
 * @property {string} [menuName] 菜单名称（不必须）类似meta的title，但优先级比它高，但是比platformAlias低
 */

/**
 * @typedef RoutePlatformAlias
 * @property {number} platformId 平台id 详看数据字典 platformTitleDict
 * @property {string} [platformName] 平台名称（不必须）
 * @property {string} [menuName] 平台展示的路由名称
 */

/**
 * @typedef RouteConfig
 * @property {string} path 路由的地址，不需要加父路由的那一部分
 * @property {string} name 路由名称，路由跳转使用
 * @property {string} component 路由对应的页面组件在文件中的路径
 * @property {number} type 路由类型（目录 | 菜单）
 * @property {RouteMetaConfig} meta 路由元信息
 * @property {Array<RouteApplicableCompanyType>} applicableCompanyTypes 适用主体（企业 | 事业单位 | 个体工商户 | 自然人），路由只会在适用的主体下面展示
 * @property {Array<RoutePlatformAlias>} platformAlias 平台别称，该路由在不同平台（云采购平台 | 省投平台等）展示不同的菜单名；类似meta的title，但优先级比它高
 * @property {Array} applicableCompanyIdentities 适用身份，详看数据字典 workbenchServiceDict
 * @property {Array} roles 适用角色，详看数据字典 platformFunctionModuleDict
 * @property {Array} superviseInstitutions 监管机构
 * @property {Array<RouteConfig>} children 子路由
 */

import Vue from 'vue'
import Router from 'vue-router'
// 导入平台默认路由
import globalRoutes from './common.js'

Vue.use(Router)

// 解决路由重复跳转报错
const originalPush = Router.prototype.push
const originalReplace = Router.prototype.replace
const originalBack = Router.prototype.back
// 修改原型对象中的push函数
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
}
// 修改原型对象中的replace函数
Router.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => err)
}
// 修改原型对象中的back函数，增加路由回退时，删除页面缓存的操作
Router.prototype.back = function back(location) {
  // store.commit('router/removeCacheView', router.currentRoute.name)
  originalBack.call(this, location)
}

const router = new Router({
  mode: 'history',
  scrollBehavior: () => ({ y: 0 }),
  routes: globalRoutes
})

export default router
